/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

class EventBus {
    constructor() {
        this.globalWares = [];
        this.targetWares = new Map();
        this.listeners = new Set();
    }
    use(mw) {
        this.globalWares.push(mw);
    }
    useFor(type, mw) {
        var _a;
        const arr = (_a = this.targetWares.get(type)) !== null && _a !== void 0 ? _a : [];
        arr.push(mw);
        this.targetWares.set(type, arr);
    }
    subscribe(listener) {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    }
    send(cmd) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            const chain = [
                ...this.globalWares,
                ...((_a = this.targetWares.get(cmd.type)) !== null && _a !== void 0 ? _a : []),
                // terminal: notify listeners
                (_cmd, _next) => __awaiter(this, void 0, void 0, function* () {
                    for (const l of this.listeners)
                        l(cmd);
                }),
            ];
            const run = (i, c) => Promise.resolve(chain[i](c, (nextCmd) => run(i + 1, nextCmd)));
            yield run(0, cmd);
        });
    }
}
const bus = new EventBus();

class ComponentRegistry {
    constructor() {
        this.registry = new Map();
    }
    register(urn, comp) {
        this.registry.set(urn, comp);
    }
    get(urn) {
        return this.registry.get(urn);
    }
    list() {
        return Array.from(this.registry.keys());
    }
}
const componentRegistry = new ComponentRegistry();

class Effects {
    constructor() {
        this.subs = [];
        this.every = new Map();
        this.latest = new Map();
        this.debouncers = new Map();
        this.throttles = new Map();
        this.runningLatest = new Map(); // key => task
    }
    start() {
        // Single subscription point; effects see commands AFTER middleware ran
        const unsub = bus.subscribe((cmd) => this.dispatch(cmd));
        this.subs.push(unsub);
    }
    stop() {
        this.subs.forEach((u) => u());
        this.subs = [];
        // cancel latest tasks
        for (const [, task] of this.runningLatest)
            task.controller.abort();
        this.runningLatest.clear();
        // clear timers
        for (const [, t] of this.debouncers)
            clearTimeout(t);
        this.debouncers.clear();
    }
    // takeEvery
    onEvery(type, handler) {
        var _a;
        const arr = (_a = this.every.get(type)) !== null && _a !== void 0 ? _a : [];
        arr.push(handler);
        this.every.set(type, arr);
    }
    // takeLatest (optionally keyed by something in the command)
    onLatest(type, handler, keyFn) {
        var _a, _b, _c;
        const bucket = (_a = this.latest.get(type)) !== null && _a !== void 0 ? _a : new Map();
        const key = keyFn ? (_b = keyFn({ type })) !== null && _b !== void 0 ? _b : "_" : "_";
        const arr = (_c = bucket.get(key)) !== null && _c !== void 0 ? _c : [];
        arr.push((cmd, ctx) => {
            // when keyFn is provided, recompute per-cmd
            handler(cmd, ctx);
        });
        bucket.set(key, arr);
        this.latest.set(type, bucket);
    }
    // debounce per key
    onDebounce(type, handler, wait, keyFn = () => "_") {
        this.onEvery(type, (cmd, ctx) => {
            const key = `${type}:${keyFn(cmd)}`;
            const prev = this.debouncers.get(key);
            if (prev)
                clearTimeout(prev);
            const t = setTimeout(() => handler(cmd, ctx), wait);
            this.debouncers.set(key, t);
        });
    }
    // throttle per key
    onThrottle(type, handler, wait, keyFn = () => "_") {
        this.onEvery(type, (cmd, ctx) => {
            var _a;
            const key = `${type}:${keyFn(cmd)}`;
            const now = Date.now();
            const last = (_a = this.throttles.get(key)) !== null && _a !== void 0 ? _a : 0;
            if (now - last >= wait) {
                this.throttles.set(key, now);
                handler(cmd, ctx);
            }
        });
    }
    // internal fan-out
    dispatch(cmd) {
        // takeEvery
        const ev = this.every.get(cmd.type);
        if (ev === null || ev === void 0 ? void 0 : ev.length)
            ev.forEach((h) => this.spawn(h, cmd));
        // takeLatest
        const bucket = this.latest.get(cmd.type);
        if (bucket && bucket.size) {
            const key = [...bucket.keys()].length === 1 ? "_" : this.computeKey(bucket, cmd);
            const handlers = bucket.get(key);
            if (handlers === null || handlers === void 0 ? void 0 : handlers.length) {
                this.cancelLatest(cmd.type, key);
                const task = this.spawnMany(handlers, cmd);
                this.runningLatest.set(this.latestKey(cmd.type, key), task);
            }
        }
    }
    computeKey(bucket, cmd) {
        var _a;
        // We stored "_" at registration time; allow dynamic keying by reading meta.key if present
        return ((_a = cmd.meta) === null || _a === void 0 ? void 0 : _a.key) || "_";
    }
    latestKey(type, key) {
        return `${type}::${key}`;
    }
    cancelLatest(type, key) {
        const k = this.latestKey(type, key);
        const prev = this.runningLatest.get(k);
        if (prev) {
            prev.controller.abort();
            this.runningLatest.delete(k);
        }
    }
    spawn(handler, cmd) {
        const controller = new AbortController();
        const ctx = {
            send: (c) => bus.send(c),
            delay: (ms) => new Promise((res, rej) => {
                const t = setTimeout(() => res(), ms);
                controller.signal.addEventListener("abort", () => {
                    clearTimeout(t);
                    rej(new DOMException("Aborted", "AbortError"));
                });
            }),
            aborted: () => controller.signal.aborted,
            //   getLocation: () => navigationStore.location,
            signal: controller.signal,
        };
        const promise = Promise.resolve(handler(cmd, ctx)).catch((e) => {
            if (controller.signal.aborted)
                return; // swallow aborts
            // eslint-disable-next-line no-console
            console.error("[effect error]", e);
        });
        return { controller, promise };
    }
    spawnMany(handlers, cmd) {
        const controller = new AbortController();
        const ctx = {
            send: (c) => bus.send(c),
            delay: (ms) => new Promise((res, rej) => {
                const t = setTimeout(() => res(), ms);
                controller.signal.addEventListener("abort", () => {
                    clearTimeout(t);
                    rej(new DOMException("Aborted", "AbortError"));
                });
            }),
            aborted: () => controller.signal.aborted,
            //   getLocation: () => navigationStore.location,
            signal: controller.signal,
        };
        const promise = Promise.all(handlers.map((h) => Promise.resolve(h(cmd, ctx)))).catch((e) => {
            if (controller.signal.aborted)
                return;
            console.error("[effect error]", e);
        });
        return { controller, promise: promise.then(() => { }) };
    }
}
const effects = new Effects();

const enableLogging = () => {
    console.log("[NAVIGATION] Logging enabled");
    bus.use((cmd, next) => __awaiter(void 0, void 0, void 0, function* () {
        console.log("[NAVIGATION CMD]", cmd);
        yield next(cmd);
    }));
};

export { bus, componentRegistry, effects, enableLogging };
//# sourceMappingURL=index.js.map
